/* ============================================
   VGTKIDS Hero — Entry Point
   GSAP entrance, WebGL init, WobbleButton
   ============================================ */

import { WobbleButton } from './wobble-button.js';

// ---- Check for reduced motion preference ----
function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

// ---- GSAP entrance choreography ----
async function animateEntrance() {
  let gsap;
  try {
    const mod = await import('https://cdn.jsdelivr.net/npm/gsap@3.12.5/+esm');
    gsap = mod.gsap || mod.default;
  } catch (err) {
    console.warn('[VGTKIDS] GSAP load failed, showing elements immediately:', err);
    document.querySelectorAll('.bouncy-hero__cta-layer, .bouncy-hero__eyebrow').forEach(el => {
      el.style.opacity = '1';
      el.style.transform = 'none';
    });
    return;
  }

  if (prefersReducedMotion()) {
    gsap.set('.bouncy-hero__cta-layer', { opacity: 1, scale: 1 });
    gsap.set('.bouncy-hero__eyebrow', { opacity: 1 });
    return;
  }

  const tl = gsap.timeline({ defaults: { ease: 'expo.out' } });

  tl.fromTo('.bouncy-hero__cta-layer',
    { opacity: 0, scale: 0.85, y: 20 },
    { opacity: 1, scale: 1, y: 0, duration: 1.2 },
    0.3
  );

  tl.fromTo('.bouncy-hero__eyebrow',
    { opacity: 0, y: 10 },
    { opacity: 1, y: 0, duration: 0.8 },
    0.6
  );

  return tl;
}

// ---- Main initialization ----
async function init() {
  const canvasBack = document.getElementById('bouncy-hero-canvas-back');
  const canvasFront = document.getElementById('bouncy-hero-canvas-front');
  const section = document.querySelector('.bouncy-hero');

  if (!canvasBack || !canvasFront || !section) {
    console.error('[VGTKIDS] Required DOM elements not found');
    return;
  }

  // 1. Setup WobbleButton
  const ctaEl = document.getElementById('hero-cta');
  let wobbleBtn = null;
  if (ctaEl) {
    try {
      wobbleBtn = new WobbleButton(ctaEl, 48);
    } catch (e) {
      console.warn('WobbleButton init failed', e);
    }
  }

  // 2. Animate text entrance immediately
  const entranceDone = animateEntrance();

  // 3. (Reduced motion bypass removed to ensure WebGL balls always show on mobile)

  // 4. Import Three.js + scene module
  let THREE, initScene;
  try {
    const threeMod = await import('https://unpkg.com/three@0.160.0/build/three.module.js');
    THREE = threeMod;
    const sceneModule = await import('./vgtkids-hero-scene.js');
    initScene = sceneModule.initScene;
  } catch (err) {
    console.error('[VGTKIDS] Three.js load failed:', err);
    return;
  }

  // 5. Initialize scene
  let sceneController;
  try {
    sceneController = await initScene(THREE, canvasBack, canvasFront);
  } catch (err) {
    console.error('[VGTKIDS] Scene init failed:', err);
    return;
  }

  // 6. Connect UI Colliders to Scene
  const headlineEl = document.querySelector('.bouncy-hero__headline-wrap');

  if (ctaEl) {
    // Swap link for mobile if specified
    const isMobile = window.innerWidth <= 767;
    if (isMobile && ctaEl.dataset.mobileLink) {
      ctaEl.href = ctaEl.dataset.mobileLink;
    } else if (!isMobile && ctaEl.dataset.desktopLink) {
      ctaEl.href = ctaEl.dataset.desktopLink;
    }
  }

  function syncUIColliders() {
    const colliders = [];
    if (headlineEl) colliders.push({ type: 'headline', rect: headlineEl.getBoundingClientRect() });
    if (ctaEl) colliders.push({ type: 'cta', rect: ctaEl.getBoundingClientRect() });

    if (sceneController) {
      sceneController.setUIColliders(colliders);
    }
  }

  // 7. WobbleButton Loop
  const pointer = { x: -999, y: -999, active: false };
  let wobbleAnimId = null;
  let prevWobbleTime = performance.now();

  function wobbleRaf(timestamp) {
    const dt = Math.min((timestamp - prevWobbleTime) / 1000, 1 / 30);
    prevWobbleTime = timestamp;

    let active = false;
    if (wobbleBtn) {
      if (wobbleBtn.update(pointer, dt)) {
        active = true;
      }
    }

    syncUIColliders();

    if (active) {
      wobbleAnimId = requestAnimationFrame(wobbleRaf);
    } else {
      wobbleAnimId = null;
    }
  }

  window.addEventListener('pointermove', (e) => {
    pointer.x = e.clientX;
    pointer.y = e.clientY;
    pointer.active = true;
    if (!wobbleAnimId) {
      prevWobbleTime = performance.now();
      wobbleAnimId = requestAnimationFrame(wobbleRaf);
    }
  });

  window.addEventListener('pointerleave', () => {
    pointer.active = false;
  });

  // 7b. Scroll-driven vertical movement (Native Scroll Parallax)
  let scrollY = 0;
  const SMOOTH_FACTOR = 0.25;

  const restingTargets = sceneController.ballConfigs.map(cfg => ({
    x: cfg.pos[0], y: cfg.pos[1], z: cfg.pos[2]
  }));

  function updateResponsiveBallPositions() {
    const isMobile = window.innerWidth <= 767;
    let baseX = 0;
    
    // Dynamically match the CTA button's exact X position on ALL devices
    if (ctaEl && sceneController && sceneController.screenToWorld) {
      const rect = ctaEl.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      const worldPos = sceneController.screenToWorld(centerX, centerY);
      
      // Set the balls directly over the center of the button so they don't fall off screen
      baseX = worldPos.x;
    }
    
    // Scale them down to 60% of original size on mobile
    if (sceneController && sceneController.setMobileScale) {
      sceneController.setMobileScale(isMobile ? 0.6 : 1.0);
    }
    
    for (let i = 0; i < restingTargets.length; i++) {
      restingTargets[i].x = baseX + (Math.random() - 0.5) * (isMobile ? 1.0 : 1.5);
    }
    
    // Also snap the actual meshes immediately so they don't slowly float over on resize
    if (sceneController && sceneController.setScrollTargets) {
      const targets = restingTargets.map((rest) => ({
        x: rest.x,
        y: rest.y + scrollY, 
        z: rest.z,
      }));
      sceneController.setScrollTargets(targets);
    }
  }

  // Initial calculation
  updateResponsiveBallPositions();

  window.addEventListener('resize', () => {
    if (wobbleBtn) wobbleBtn.resize();
    syncUIColliders();
    updateResponsiveBallPositions();
  });

  // Smooth update loop — runs inside the existing rAF via a hook
  const origStart = sceneController.start.bind(sceneController);
  const origResume = sceneController.resume.bind(sceneController);

  function smoothWheelUpdate() {
    // Calculate progress through the sticky scroll track
    const track = document.querySelector('.hero-scroll-track');
    let scrollProgress = 0;
    if (track) {
      // The distance the user scrolls while the section is pinned
      const rect = track.getBoundingClientRect();
      const maxScroll = track.offsetHeight - window.innerHeight;
      
      // Calculate how far we've scrolled past the top of the track
      // (rect.top is 0 when track is exactly at the top of the viewport)
      const scrollDistance = -rect.top;
      
      if (maxScroll > 0) {
        scrollProgress = Math.max(0, Math.min(scrollDistance / maxScroll, 1));
      }
    }

    // Map progress 0->1 to a 3D Y offset (24.0 makes them fly up much faster)
    const targetScrollY = scrollProgress * 24.0;

    // Smooth lerp toward target
    const prev = scrollY;
    scrollY += (targetScrollY - scrollY) * SMOOTH_FACTOR;

    // Only send new targets if progress actually changed
    if (Math.abs(scrollY - prev) > 0.001) {
      const targets = restingTargets.map((rest) => ({
        x: rest.x,
        y: rest.y + scrollY, // As user scrolls down, balls move UP in 3D space
        z: rest.z,
      }));
      sceneController.setScrollTargets(targets);
    }

    requestAnimationFrame(smoothWheelUpdate);
  }
  requestAnimationFrame(smoothWheelUpdate);

  // 8. Wait for entrance animation
  await entranceDone;
  await new Promise(r => setTimeout(r, 200));

  // Re-calculate positions now that CSS and GSAP animations are fully settled
  updateResponsiveBallPositions();

  // 9. Start render loop
  sceneController.start();

  // 10. Reveal canvases
  requestAnimationFrame(() => {
    canvasBack.classList.add('is-ready');
    canvasFront.classList.add('is-ready');
  });

  // 11. IntersectionObserver: pause when off-screen
  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          sceneController.resume();
        } else {
          sceneController.pause();
        }
      }
    },
    { threshold: 0.05 }
  );
  observer.observe(section);

  // 12. Reduced motion change at runtime
  window.matchMedia('(prefers-reduced-motion: reduce)').addEventListener('change', (e) => {
    if (e.matches) {
      sceneController.pause();
      canvasBack.classList.remove('is-ready');
      canvasFront.classList.remove('is-ready');
    } else {
      sceneController.resume();
      canvasBack.classList.add('is-ready');
      canvasFront.classList.add('is-ready');
    }
  });

  // 13. Pause on tab hide
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      sceneController.pause();
    } else {
      sceneController.resume();
    }
  });
}

// ---- Bootstrap ----
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
